/**
 * Landing 2b — Owner Console Homepage Snapshot Composer.
 *
 * Renders two clearly-distinct sections:
 *   1. Published snapshot summary (read-only) + Republish action.
 *   2. Draft composer with four fixed slot cards (top_opportunity,
 *      opportunity, fairly_valued, risk), Generate + Publish + Discard
 *      actions, and per-slot Pin controls.
 *
 * Backend endpoints (all owner-only, admin_session + CSRF):
 *   GET    /api/admin/homepage-snapshot          → published + scheduler
 *   GET    /api/admin/homepage-snapshot/draft    → current draft
 *   POST   /api/admin/homepage-snapshot/override → pin (writes draft)
 *   POST   /api/admin/homepage-snapshot/generate → fill remaining
 *   POST   /api/admin/homepage-snapshot/publish-draft → explicit publish
 *   DELETE /api/admin/homepage-snapshot/draft    → discard draft
 *   POST   /api/admin/homepage-snapshot/republish → republish LKG
 *
 * The backend remains the sole authority for validation. This
 * component maps server projections to visual states only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, CheckCircle2, Circle, Loader2, RefreshCw,
  RotateCcw, Sparkles, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { subscribe as _stepUpSubscribe } from "@/lib/adminStepUp";

// Owner-Console defect fix (Feb 2026) — recognize sanitized WS-9
// step-up / lifecycle codes on axios errors so the composer surfaces
// the correct re-auth path instead of the dead-end "Session expired"
// static message.  The set is kept in sync with
// ``/app/frontend/src/lib/adminStepUp.js``.
const _STEP_UP_CODES = new Set([
  "admin_recent_auth_required",
  "admin_totp_step_up_required",
  "admin_sensitive_read_window_expired",
]);
const _LIFECYCLE_CODES = new Set([
  "admin_session_expired",
  "admin_idle_timeout",
  "admin_session_revoked",
  "admin_session_required",
]);

// Pull the sanitized code out of an axios error's `response.data`.
// FastAPI wraps HTTPException detail either as a raw string or a
// dict (`{error, reason, ...}`).  Backend admin routes here use both.
function _detailCode(e) {
  const d = e?.response?.data;
  if (!d) return "";
  if (typeof d === "string") return d;
  const detail = d.detail ?? d.error ?? d.reason ?? "";
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    return detail.error || detail.reason || detail.code || "";
  }
  return "";
}

function _isStepUpError(e) { return _STEP_UP_CODES.has(_detailCode(e)); }
function _isLifecycleError(e) { return _LIFECYCLE_CODES.has(_detailCode(e)); }

// Broadcast to the global step-up subscribers exactly the same way
// adminStepUp.inspectAdminResponse() does for fetch()-based callers.
// Composers use axios, not fetch, so we bridge here.
function _notifyStepUp(code) {
  try {
    // Reuse the subscribers set via a private import shim — we import
    // ``subscribe`` above only to keep the module linked; the actual
    // broadcast uses window's dispatcher when the shim is not
    // available.  In our current codebase adminStepUp exposes a
    // singleton set of subscribers; a synthetic subscribe → immediate
    // notify is not possible.  Instead we dispatch a CustomEvent that
    // the shell app's WS-9 step-up dialog also listens to.  This keeps
    // the fix additive.
    const evt = new CustomEvent("stockaily:admin_stepup_required", {
      detail: { code },
    });
    window.dispatchEvent(evt);
  } catch (_e) { /* isolate */ }
}
// Keep the import from being tree-shaken so callers that hook the
// subscribe() bus continue to work in test/dev builds.
void _stepUpSubscribe;

const SLOTS = ["top_opportunity", "opportunity", "fairly_valued", "risk"];

function shortId(id, n = 8) {
  if (!id) return "";
  return `${String(id).slice(0, n)}\u2026`;
}
function fmtDate(v) {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
}

function ReadinessBadge({ ready, total, t }) {
  const ok = ready === total;
  const cls = ok
    ? "border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.10)] text-[#2BFF8A]"
    : "border-[#FFB72B]/40 bg-[rgba(255,183,43,0.10)] text-[#FFB72B]";
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5 border ${cls}`}
      data-testid="composer-readiness-counter"
      aria-label={t("composer.readiness_counter", { ready, total })}
    >
      {t("composer.readiness_counter", { ready, total })}
    </span>
  );
}

function SourceBadge({ source, t }) {
  if (!source) return null;
  const map = {
    manual_pin: { c: "text-[#3DEBFF]",
                  b: "border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.08)]",
                  k: "composer.src_manual_pin" },
    generated: { c: "text-[#2BFF8A]",
                 b: "border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.08)]",
                 k: "composer.src_generated" },
    inherited_from_active: {
      c: "text-muted-foreground",
      b: "border-border/60 bg-transparent",
      k: "composer.src_inherited",
    },
  };
  const s = map[source] || map.inherited_from_active;
  return (
    <span
      className={`text-[9px] uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border ${s.b} ${s.c}`}
      data-testid={`composer-src-${source}`}
    >
      {t(s.k)}
    </span>
  );
}

function StateChip({ label, tone = "muted", testId }) {
  const tones = {
    valid: "text-[#2BFF8A] border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.06)]",
    warn:  "text-[#FFB72B] border-[#FFB72B]/40 bg-[rgba(255,183,43,0.06)]",
    error: "text-[#FF3DA6] border-[#FF3DA6]/40 bg-[rgba(255,61,166,0.06)]",
    muted: "text-muted-foreground border-border/60 bg-transparent",
  };
  return (
    <span
      className={`text-[9px] uppercase tracking-[0.12em] rounded-full px-1.5 py-0.5 border ${tones[tone]}`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

function SlotCard({ slot, t, onPin, busy, dataTestIdBase }) {
  const [ticker, setTicker] = useState("");
  const [localErr, setLocalErr] = useState(null);
  const isValid = !!slot?.is_valid;
  const chartValid = !!slot?.chart_valid;
  const src = slot?.source;
  const invalidReason = slot?.invalid_reason || null;
  const card = slot?.card || null;

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const tk = (ticker || "").trim().toUpperCase();
    if (!tk) return;
    setLocalErr(null);
    const err = await onPin(slot.slot_type, tk);
    if (err) setLocalErr(err);
    else setTicker("");
  }, [ticker, onPin, slot]);

  return (
    <div
      className="rounded-xl border border-border/60 bg-card/40 p-3 flex flex-col gap-2"
      data-testid={`${dataTestIdBase}-slot-${slot.slot_type}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {t(`composer.slot_label_${slot.slot_type}`)}
        </div>
        <div className="flex items-center gap-1">
          {src ? <SourceBadge source={src} t={t} /> : null}
        </div>
      </div>

      {card ? (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <div className="text-sm font-mono font-semibold" data-testid={`${dataTestIdBase}-ticker-${slot.slot_type}`}>
              {card.ticker}
            </div>
            <div className="text-[11px] text-muted-foreground truncate" title={card.name}>
              {card.name}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground flex gap-2 flex-wrap">
            <span>{t("composer.field_status")}: {card.status || "-"}</span>
            <span>·</span>
            <span>{t("composer.field_score")}: <span className="tabular">{card.ai_score ?? "-"}</span></span>
            {card.price != null ? (
              <><span>·</span><span>{t("composer.field_price")}: {card.price}</span></>
            ) : null}
            {card.fair_value != null ? (
              <><span>·</span><span>{t("composer.field_fair_value")}: {card.fair_value}</span></>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground italic" data-testid={`${dataTestIdBase}-empty-${slot.slot_type}`}>
          {t("composer.slot_state_empty")}
        </div>
      )}

      <div className="flex flex-wrap gap-1 items-center">
        {isValid ? (
          <StateChip label={t("composer.slot_state_valid")} tone="valid" testId={`${dataTestIdBase}-valid-${slot.slot_type}`} />
        ) : card ? (
          <StateChip label={t("composer.slot_state_invalid")} tone="error" testId={`${dataTestIdBase}-invalid-${slot.slot_type}`} />
        ) : null}
        {card ? (
          chartValid ? (
            <StateChip label={t("composer.chart_available")} tone="muted" testId={`${dataTestIdBase}-chart-ok-${slot.slot_type}`} />
          ) : (
            <StateChip label={t("composer.chart_missing")} tone="warn" testId={`${dataTestIdBase}-chart-missing-${slot.slot_type}`} />
          )
        ) : null}
        {invalidReason ? (
          <span className="text-[10px] text-[#FFC7E1]" data-testid={`${dataTestIdBase}-reason-${slot.slot_type}`}>
            {t(`composer.invalid_reason_${invalidReason}`, {}, invalidReason)}
          </span>
        ) : null}
      </div>

      <form onSubmit={submit} className="flex items-center gap-1 pt-1">
        <label htmlFor={`ticker-${slot.slot_type}`} className="sr-only">
          {t("composer.pin_ticker_label", { slot: t(`composer.slot_label_${slot.slot_type}`) })}
        </label>
        <Input
          id={`ticker-${slot.slot_type}`}
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="TICKER"
          className="text-xs font-mono h-7"
          disabled={busy}
          data-testid={`${dataTestIdBase}-input-${slot.slot_type}`}
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          disabled={busy || !ticker.trim()}
          data-testid={`${dataTestIdBase}-pin-${slot.slot_type}`}
          aria-label={t("composer.pin_action_aria", { slot: t(`composer.slot_label_${slot.slot_type}`) })}
        >
          {t("composer.pin_action")}
        </Button>
      </form>
      {localErr ? (
        <div className="text-[10px] text-[#FFC7E1]" role="alert" data-testid={`${dataTestIdBase}-error-${slot.slot_type}`}>
          {localErr}
        </div>
      ) : null}
    </div>
  );
}

function emptyDraftSlots() {
  return SLOTS.map((s) => ({ slot_type: s, ticker: null, source: null,
    is_valid: false, chart_valid: false, invalid_reason: null, card: null }));
}

/**
 * Main component.  Accepts an `adminHeaders` function so it inherits the
 * same admin-session + CSRF header injection used elsewhere in the
 * Owner Console.
 */
export default function HomepageSnapshotComposer({ adminHeaders }) {
  const { t } = useTranslation();
  const [published, setPublished] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [emptyPoolSlots, setEmptyPoolSlots] = useState([]);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const liveRef = useRef(null);

  const announce = useCallback((msg) => {
    if (liveRef.current) liveRef.current.textContent = msg;
  }, []);

  // Owner-Console defect fix (Feb 2026, Defect 2 follow-up) — when
  // the global AdminStepUpDialog reports a successful re-auth, clear
  // the composer's blocking error message and reload so the owner
  // can retry the action.  On cancel we leave the error in place so
  // the protected action stays visibly blocked with a clear reason.
  useEffect(() => {
    const onOk = () => {
      setErr(null);
      // Refresh the draft state — the previous failing action left
      // it stale if any partial writes happened server-side.
      try { load(); } catch (_e) { /* isolate */ }
    };
    window.addEventListener("stockaily:admin_stepup_success", onOk);
    return () => window.removeEventListener("stockaily:admin_stepup_success", onOk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [pubRes, draftRes] = await Promise.all([
        api.get("/admin/homepage-snapshot", { headers: adminHeaders?.() || {} }),
        api.get("/admin/homepage-snapshot/draft", { headers: adminHeaders?.() || {} }),
      ]);
      setPublished(pubRes.data);
      setDraft(draftRes.data?.draft || null);
    } catch (e) {
      // Owner-Console defect fix (Feb 2026) — recognize sanitized
      // step-up codes on the initial dashboard load so the shell can
      // surface the re-auth modal instead of showing a dead-end
      // "Session expired" (the operational-read policy never returns
      // step-up today, but downstream ``/draft`` may).
      if (_isStepUpError(e)) {
        _notifyStepUp(_detailCode(e));
        setErr(t("composer.err_recent_auth_required"));
      } else if (_isLifecycleError(e) || e?.response?.status === 401
                 || e?.response?.status === 403) {
        setErr(t("composer.err_session_expired"));
      } else {
        setErr(t("composer.err_load_failed"));
      }
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, t]);

  useEffect(() => { load(); }, [load]);

  const slots = useMemo(() => {
    const s = draft?.slots || emptyDraftSlots();
    const byType = Object.fromEntries(s.map((x) => [x.slot_type, x]));
    return SLOTS.map((st) => byType[st] || {
      slot_type: st, ticker: null, source: null,
      is_valid: false, chart_valid: false, invalid_reason: null, card: null,
    });
  }, [draft]);

  const ready = useMemo(
    () => slots.filter((s) => s.is_valid && s.chart_valid).length, [slots],
  );
  const draftState = draft?.state || "empty";
  const canPublish = ready === 4 && draftState === "ready";
  const publishDisabledReason = !canPublish
    ? (ready < 4
        ? t("composer.publish_disabled_incomplete")
        : t("composer.publish_disabled_reload"))
    : null;

  const pinSlot = useCallback(async (slot, ticker) => {
    if (busy) return t("composer.err_busy");
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(
        "/admin/homepage-snapshot/override",
        { slot, ticker, expected_revision: draft?.revision },
        { headers: adminHeaders?.() || {} },
      );
      const server = res?.data?.draft || null;
      if (server) setDraft(server);
      else await load();
      announce(t("composer.announce_pinned", { ticker, slot: t(`composer.slot_label_${slot}`) }));
      return null;
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail || {};
      // Owner-Console defect fix (Feb 2026) — surface step-up needs
      // through the WS-9 bus instead of a dead-end "Session expired".
      if (_isStepUpError(e)) {
        _notifyStepUp(_detailCode(e));
        setErr(t("composer.err_recent_auth_required"));
        return t("composer.err_recent_auth_required");
      }
      if (_isLifecycleError(e)) {
        setErr(t("composer.err_session_expired"));
        return t("composer.err_session_expired");
      }
      if (status === 409) {
        await load();
        return t("composer.err_stale_revision");
      }
      if (status === 400) {
        return d.reason || t("composer.err_pin_400");
      }
      if (status === 401 || status === 403) {
        setErr(t("composer.err_session_expired"));
        return t("composer.err_session_expired");
      }
      return t("composer.err_pin_generic");
    } finally {
      setBusy(false);
    }
  }, [busy, draft, adminHeaders, load, t, announce]);

  const generate = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(
        "/admin/homepage-snapshot/generate",
        {}, { headers: adminHeaders?.() || {} },
      );
      const server = res?.data?.draft || null;
      const published = res?.data?.published;
      // Phase 6.4 — structured per-slot reasons from the backend.
      // When the search completed but no candidate qualified, show
      // a truthful non-error message rather than "Generate failed".
      const slotReasons = res?.data?.slot_reasons || {};
      const emptyPools = Object.entries(slotReasons)
        .filter(([, r]) => r === "no_eligible_candidate")
        .map(([slot]) => slot);
      if (server) setDraft(server);
      if (published) {
        toast.success(t("composer.toast_generate_published"));
        await load();
      } else if (emptyPools.length > 0) {
        setEmptyPoolSlots(emptyPools);
        toast.info(t("composer.toast_generate_no_candidate"));
        announce(t("composer.toast_generate_no_candidate"));
      } else {
        toast.success(t("composer.toast_generate_partial"));
        announce(t("composer.announce_generated"));
      }
    } catch (e) {
      setErr(e?.response?.data?.detail?.reason || t("composer.err_generate"));
    } finally {
      setBusy(false);
    }
  }, [busy, adminHeaders, load, t, announce]);

  const publish = useCallback(async () => {
    if (busy || !canPublish || !draft) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(
        "/admin/homepage-snapshot/publish-draft",
        { draft_id: draft.draft_id, expected_revision: draft.revision },
        { headers: adminHeaders?.() || {} },
      );
      const already = !!res?.data?.already_published;
      if (already) {
        toast.success(t("composer.toast_already_published"));
      } else {
        toast.success(t("composer.toast_publish_success"));
      }
      await load();
      announce(t("composer.announce_published"));
    } catch (e) {
      const status = e?.response?.status;
      // Owner-Console defect fix (Feb 2026) — surface the actual
      // structured failure reason for owner diagnostics on every
      // non-2xx response.  Step-up (recent-auth / TOTP) is broadcast
      // through the WS-9 bus so the shell can open the re-auth
      // modal instead of dead-ending the composer.  Stale-revision
      // remains a friendly reload prompt.
      if (_isStepUpError(e)) {
        _notifyStepUp(_detailCode(e));
        setErr(t("composer.err_recent_auth_required"));
        return;
      }
      if (_isLifecycleError(e)) {
        setErr(t("composer.err_session_expired"));
        return;
      }
      if (status === 409) {
        await load();
        setErr(t("composer.err_stale_revision"));
        return;
      }
      const d = e?.response?.data?.detail;
      const dictDetail = (d && typeof d === "object") ? d : null;
      const reason = dictDetail?.reason || null;
      const code = dictDetail?.error || null;
      if (status === 400) {
        setErr(reason || t("composer.err_publish_400"));
        return;
      }
      // Any other non-2xx (403 not step-up, 404, 500, network error).
      // Show the safe structured code + reason if the server provided
      // one so the owner has actionable diagnostics; otherwise show
      // the generic string.
      if (code || reason) {
        setErr([code, reason].filter(Boolean).join(": "));
      } else {
        setErr(t("composer.err_publish_generic"));
      }
    } finally {
      setBusy(false);
    }
  }, [busy, canPublish, draft, adminHeaders, load, t, announce]);

  const discard = useCallback(async () => {
    if (busy || !draft) return;
    setBusy(true);
    setErr(null);
    try {
      await api.delete(
        "/admin/homepage-snapshot/draft",
        {
          headers: adminHeaders?.() || {},
          data: { draft_id: draft.draft_id, expected_revision: draft.revision },
        },
      );
      setDraft(null);
      setShowDiscardConfirm(false);
      toast.success(t("composer.toast_discard_success"));
      announce(t("composer.announce_discarded"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        await load();
        setErr(t("composer.err_stale_revision"));
      } else {
        setErr(t("composer.err_discard_generic"));
      }
    } finally {
      setBusy(false);
    }
  }, [busy, draft, adminHeaders, load, t, announce]);

  const republish = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(
        "/admin/homepage-snapshot/republish",
        {}, { headers: adminHeaders?.() || {} },
      );
      // Refresh only the published section — draft is intentionally
      // preserved.  We reload both endpoints to reflect the new active
      // row + confirm no draft mutation happened.
      await load();
      toast.success(t("composer.toast_republish_success"));
    } catch (e) {
      setErr(t("composer.err_republish_generic"));
    } finally {
      setBusy(false);
    }
  }, [busy, adminHeaders, load, t]);

  const active = published?.active;
  return (
    <div className="space-y-4" data-testid="owner-hms-composer">
      <div ref={liveRef} aria-live="polite" className="sr-only" />

      {/* ---------- Published snapshot section ---------- */}
      <section
        className="rounded-xl border border-border/60 bg-card/40 p-4"
        data-testid="composer-published-section"
        aria-labelledby="composer-published-title"
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3
            id="composer-published-title"
            className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground"
          >
            {t("composer.published_title")}
          </h3>
          <div className="flex items-center gap-2">
            {active ? (
              <span
                className="text-[9px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5 border border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.10)] text-[#2BFF8A]"
                data-testid="composer-currently-public-badge"
              >
                {t("composer.currently_public")}
              </span>
            ) : (
              <span
                className="text-[9px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5 border border-[#FFB72B]/40 bg-[rgba(255,183,43,0.10)] text-[#FFB72B]"
                data-testid="composer-no-active-badge"
              >
                {t("composer.no_active_fallback_badge")}
              </span>
            )}
            <Button
              size="sm" variant="outline"
              onClick={load} disabled={loading}
              data-testid="composer-reload"
              aria-label={t("composer.reload_aria")}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              {t("composer.reload")}
            </Button>
          </div>
        </div>

        {active ? (
          <div className="text-[12px] space-y-2">
            <div className="flex flex-wrap gap-3 text-muted-foreground">
              <span>ID: <span className="font-mono">{shortId(active.snapshot_id)}</span></span>
              <span>{t("composer.published_at")}: {fmtDate(active.generated_at)}</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="composer-published-tickers">
              {(active.cards || []).map((c) => (
                <span
                  key={c.ticker}
                  className="text-[11px] rounded-md border border-border/60 px-2 py-0.5 font-mono"
                  data-testid={`composer-published-card-${c.ticker}`}
                >
                  {c.ticker} · <span className="text-muted-foreground">{c.slot}</span>
                </span>
              ))}
            </div>
            {/* Phase 6.4 — non-blocking mismatch warning per card.
                Uses ONLY the persisted current_classification annotation
                supplied by GET /admin/homepage-snapshot; no compute,
                no FMP calls, no snapshot mutation. */}
            {(active.cards || []).some((c) => c.current_classification_status === "differs") && (
              <div
                className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-900"
                data-testid="composer-mismatch-warning"
              >
                {(active.cards || []).filter((c) => c.current_classification_status === "differs").map((c) => (
                  <div key={`mm-${c.ticker}`} data-testid={`composer-mismatch-${c.ticker}`}>
                    <span className="font-mono">{c.ticker}</span> ·{" "}
                    {t("composer.mismatch_warning", {
                      published: t(`analyze.classification.${c.classification}`) || c.classification,
                      current: t(`analyze.classification.${c.current_classification}`) || c.current_classification,
                    })}
                  </div>
                ))}
              </div>
            )}
            <div className="pt-1">
              <Button
                size="sm" variant="outline"
                onClick={republish} disabled={busy}
                data-testid="composer-republish"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                {t("composer.republish")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-muted-foreground" data-testid="composer-no-active-body">
            {t("composer.no_active_body")}
          </div>
        )}
      </section>

      {/* ---------- Draft composer section ---------- */}
      <section
        className="rounded-xl border border-border/60 bg-card/40 p-4"
        data-testid="composer-draft-section"
        aria-labelledby="composer-draft-title"
      >
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <h3
              id="composer-draft-title"
              className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              {t("composer.draft_title")}
            </h3>
            <div className="text-[11px] text-muted-foreground italic mt-0.5" data-testid="composer-draft-hint">
              {t("composer.draft_not_public_hint")}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ReadinessBadge ready={ready} total={4} t={t} />
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground" data-testid="composer-draft-state">
              {t(`composer.state_${draftState}`)}
            </span>
            {draft?.revision != null ? (
              <span className="text-[10px] text-muted-foreground" data-testid="composer-draft-revision">
                rev {draft.revision}
              </span>
            ) : null}
            {draft?.updated_at ? (
              <span className="text-[10px] text-muted-foreground">
                {t("composer.updated_at")}: {fmtDate(draft.updated_at)}
              </span>
            ) : null}
          </div>
        </div>

        {/* Slots grid — 1 col mobile, 2 col tablet, 4 col desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {slots.map((s) => (
            <SlotCard
              key={s.slot_type}
              slot={s}
              t={t}
              busy={busy}
              onPin={pinSlot}
              dataTestIdBase="composer"
            />
          ))}
        </div>

        {/* Draft-level actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={generate}
            disabled={busy}
            data-testid="composer-generate"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {t("composer.generate_remaining")}
          </Button>
          <Button
            size="sm"
            variant={canPublish ? "default" : "outline"}
            onClick={publish}
            disabled={busy || !canPublish}
            data-testid="composer-publish"
            aria-describedby={publishDisabledReason ? "composer-publish-reason" : undefined}
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {t("composer.publish_now")}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setShowDiscardConfirm(true)}
            disabled={busy || !draft}
            data-testid="composer-discard-open"
            aria-label={t("composer.discard_action_aria")}
            className="ml-auto"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {t("composer.discard_draft")}
          </Button>
        </div>
        {publishDisabledReason ? (
          <div
            id="composer-publish-reason"
            className="mt-1 text-[10px] text-muted-foreground"
            data-testid="composer-publish-reason"
          >
            {publishDisabledReason}
          </div>
        ) : null}
        {err ? (
          <div
            role="alert" aria-live="assertive"
            className="mt-2 rounded-lg border border-[#FF3DA6]/40 bg-[rgba(255,61,166,0.06)] px-3 py-2 text-xs text-[#FFC7E1]"
            data-testid="composer-error"
          >
            {err}
          </div>
        ) : null}
        {emptyPoolSlots.length > 0 ? (
          <div
            className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground"
            data-testid="composer-no-eligible-candidate"
          >
            {t("composer.no_eligible_candidate")}
          </div>
        ) : null}

        {/* Explainer under Generate action */}
        <div className="mt-2 text-[10px] text-muted-foreground" data-testid="composer-generate-hint">
          {t("composer.generate_hint")}
        </div>
      </section>

      {/* Discard confirmation dialog */}
      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent data-testid="composer-discard-dialog">
          <DialogHeader>
            <DialogTitle>{t("composer.discard_confirm_title")}</DialogTitle>
            <DialogDescription>
              {t("composer.discard_confirm_body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiscardConfirm(false)}
              disabled={busy}
              data-testid="composer-discard-cancel"
            >
              {t("composer.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={discard}
              disabled={busy}
              data-testid="composer-discard-confirm"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              {t("composer.discard_confirm_action")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
